import { useState } from 'react';
import { extractAttributesList } from '@/utils/api-client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { promptService, type Prompt } from '@/services/prompt-service';
import {
  Empty,
  Spinner,
  Button,
  Badge,
  Textarea,
  toast,
} from 'prosci-ui';

export function PromptsTab() {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['prompts'],
    queryFn: () => promptService.list(),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, value }: { id: string; value: string }) =>
      promptService.update(id, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompts'] });
      toast.success('Pending change saved');
      setEditingId(null);
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to update'),
  });

  const publishMutation = useMutation({
    mutationFn: (id: string) => promptService.publish(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompts'] });
      toast.success('Published');
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to publish'),
  });

  const discardMutation = useMutation({
    mutationFn: (id: string) => promptService.discard(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompts'] });
      toast.success('Discarded');
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to discard'),
  });

  const publishAllMutation = useMutation({
    mutationFn: () => promptService.publishAll(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompts'] });
      toast.success('All changes published');
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to publish all'),
  });

  const prompts: Prompt[] =
    extractAttributesList(data);

  const hasPendingChanges = prompts.some((p) => p.has_pending_changes);

  const startEdit = (prompt: Prompt) => {
    setEditingId(prompt.id);
    setEditValue(prompt.has_pending_changes ? (prompt.pending_value ?? '') : (prompt.published_value ?? ''));
  };

  if (isLoading) {
    return <Empty><Spinner /></Empty>;
  }

  return (
    <div>
      {hasPendingChanges && (
        <div className="flex items-center justify-end gap-2 mb-4">
          <Badge variant="outline" className="border-amber-400 text-amber-700">
            Unpublished changes
          </Badge>
          <Button
            size="sm"
            onClick={() => publishAllMutation.mutate()}
            disabled={publishAllMutation.isPending}
          >
            {publishAllMutation.isPending ? 'Publishing...' : 'Publish All'}
          </Button>
        </div>
      )}

      <div className="space-y-6">
        {prompts.map((prompt) => (
          <PromptRow
            key={prompt.id}
            prompt={prompt}
            isEditing={editingId === prompt.id}
            editValue={editingId === prompt.id ? editValue : ''}
            onStartEdit={() => startEdit(prompt)}
            onCancelEdit={() => setEditingId(null)}
            onSave={(value) => updateMutation.mutate({ id: prompt.id, value })}
            onEditValueChange={setEditValue}
            onPublish={() => publishMutation.mutate(prompt.id)}
            onDiscard={() => discardMutation.mutate(prompt.id)}
            isSaving={updateMutation.isPending}
          />
        ))}
      </div>
    </div>
  );
}

function PromptRow({
  prompt,
  isEditing,
  editValue,
  onStartEdit,
  onCancelEdit,
  onSave,
  onEditValueChange,
  onPublish,
  onDiscard,
  isSaving,
}: {
  prompt: Prompt;
  isEditing: boolean;
  editValue: string;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: (value: string) => void;
  onEditValueChange: (value: string) => void;
  onPublish: () => void;
  onDiscard: () => void;
  isSaving: boolean;
}) {
  const displayValue = prompt.has_pending_changes ? prompt.pending_value : prompt.published_value;

  return (
    <div className={`rounded-lg border p-4 ${prompt.has_pending_changes ? 'border-amber-300 bg-amber-50/40 dark:bg-amber-950/10' : ''}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold">{prompt.label || prompt.key}</h3>
          {prompt.has_pending_changes && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-400 text-amber-600">
              pending
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <Button variant="ghost" size="sm" className="text-xs h-7" onClick={onCancelEdit}>
                Cancel
              </Button>
              <Button size="sm" className="text-xs h-7" onClick={() => onSave(editValue)} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </>
          ) : (
            <>
              {prompt.has_pending_changes && (
                <>
                  <Button variant="ghost" size="sm" className="text-xs h-7" onClick={onDiscard}>
                    Discard
                  </Button>
                  <Button variant="outline" size="sm" className="text-xs h-7" onClick={onPublish}>
                    Publish
                  </Button>
                </>
              )}
              <Button variant="outline" size="sm" className="text-xs h-7" onClick={onStartEdit}>
                Edit
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Description */}
      {prompt.description && (
        <p className="text-xs text-muted-foreground mb-3">{prompt.description}</p>
      )}

      {/* Content */}
      {isEditing ? (
        <Textarea
          className="font-mono text-sm min-h-[300px]"
          value={editValue}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onEditValueChange(e.target.value)}
          autoFocus
        />
      ) : (
        <div
          className="font-mono text-sm bg-muted/50 rounded p-3 max-h-[200px] overflow-y-auto whitespace-pre-wrap cursor-pointer hover:bg-muted/80 transition-colors"
          onClick={onStartEdit}
        >
          {displayValue || <span className="text-muted-foreground italic">No prompt configured</span>}
        </div>
      )}
    </div>
  );
}
